// WebAudio-synthesized SFX only — no samples (TECH_SPEC §4.5).
// The AudioContext starts suspended on mobile; the first gesture resumes it.
//
// Synthesis is written as *pure graph builders*: `blip` / `noise` / `scheduleSfx`
// take `(ctx, destination, at)` and touch no instance state, so the identical
// graph can be built on the live AudioContext (below) or on an
// OfflineAudioContext for offline evaluation (`npm run visual:audio`, see
// tools/visual/README.md). The live path is unchanged — `AudioKit` still owns the
// one shared context and the master gain.

export type Sfx = 'jump' | 'slide' | 'overtake' | 'death' | 'nearMiss' | 'revive' | 'count';

/** Every SFX the game can play, in the order the evaluation tool reports them. */
export const SFX_NAMES: readonly Sfx[] = [
  'jump',
  'slide',
  'overtake',
  'death',
  'nearMiss',
  'revive',
  'count',
];

const MASTER_VOLUME = 0.28;

/** Nodes are stopped this long after their envelope ends (ramp tails are never
 *  exactly zero, so cutting on `dur` would click). */
const RELEASE = 0.02;

// --- voices (pure: ctx + destination + start time in, stop time out) ---------

/** Osc from f0 → f1 with a percussive envelope. Returns when it stops. */
function blip(
  ctx: BaseAudioContext,
  dest: AudioNode,
  at: number,
  type: OscillatorType,
  f0: number,
  f1: number,
  dur: number,
  gain: number,
): number {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(f0, at);
  if (f1 !== f0) osc.frequency.exponentialRampToValueAtTime(Math.max(20, f1), at + dur);
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(gain, at + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  osc.connect(g);
  g.connect(dest);
  osc.start(at);
  osc.stop(at + dur + RELEASE);
  return at + dur + RELEASE;
}

/** Filtered white noise: swishes and whooshes. Returns when it stops. */
function noise(
  ctx: BaseAudioContext,
  dest: AudioNode,
  at: number,
  dur: number,
  cutoff: number,
  gain: number,
  endCutoff?: number,
  q = 0.8,
): number {
  const frames = Math.max(1, Math.floor(ctx.sampleRate * dur));
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.Q.value = q;
  filter.frequency.setValueAtTime(cutoff, at);
  if (endCutoff !== undefined) {
    filter.frequency.exponentialRampToValueAtTime(Math.max(60, endCutoff), at + dur);
  }
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(gain, at + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  src.connect(filter);
  filter.connect(g);
  g.connect(dest);
  src.start(at);
  src.stop(at + dur + RELEASE);
  return at + dur + RELEASE;
}

/**
 * The whole sound design, in one place. Builds `sfx`'s graph on `ctx` into
 * `dest` starting at `at`, and returns the time its last node stops (the tail an
 * offline render has to allocate for). No instance state, no `ctx.currentTime` —
 * both callers supply their own clock.
 */
export function scheduleSfx(sfx: Sfx, ctx: BaseAudioContext, dest: AudioNode, at: number): number {
  switch (sfx) {
    case 'jump':
      return blip(ctx, dest, at, 'square', 340, 720, 0.11, 0.5);
    case 'slide': {
      const swish = noise(ctx, dest, at, 0.18, 900, 0.45);
      const body = blip(ctx, dest, at, 'sine', 200, 120, 0.16, 0.35);
      return Math.max(swish, body);
    }
    case 'overtake': {
      blip(ctx, dest, at, 'triangle', 523, 523, 0.09, 0.5);
      blip(ctx, dest, at + 0.07, 'triangle', 659, 659, 0.09, 0.5);
      blip(ctx, dest, at + 0.14, 'triangle', 784, 784, 0.14, 0.55);
      return blip(ctx, dest, at + 0.21, 'triangle', 1047, 1047, 0.2, 0.5);
    }
    case 'death': {
      const body = blip(ctx, dest, at, 'sawtooth', 220, 55, 0.42, 0.6);
      const thud = noise(ctx, dest, at, 0.12, 400, 0.35);
      return Math.max(body, thud);
    }
    case 'nearMiss':
      // Audio-eval finding (2026-08-17): at gain 0.4 / Q 0.8 the signature
      // near-miss beat was 13 dB under every other SFX — inaudible on a phone —
      // and the sweep read as a shapeless burst. Louder, and Q 5 makes the
      // 2.6k→600 Hz fall a visible (and audible) whoosh ridge.
      return noise(ctx, dest, at, 0.32, 2600, 2.1, 600, 5);
    case 'revive':
      return blip(ctx, dest, at, 'sine', 392, 784, 0.3, 0.5);
    case 'count':
      return blip(ctx, dest, at, 'sine', 660, 660, 0.08, 0.4);
  }
}

// --- live playback -----------------------------------------------------------

/** iOS (incl. iPadOS reporting as Mac with touch) needs special handling. */
function isIOS(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

/** A minimal valid silent WAV — looped in an <audio> element on iOS to switch
 *  the app to the media-playback audio session, so the RINGER SWITCH stops
 *  muting WebAudio (the standard "unmute" trick; most phones sit on silent). */
const SILENT_WAV =
  'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAAAA';

export class AudioKit {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private muted = false;
  private mediaShim: HTMLAudioElement | null = null;

  private ensure(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const Ctor: typeof AudioContext | undefined =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    try {
      const ctx = new Ctor();
      const master = ctx.createGain();
      master.gain.value = MASTER_VOLUME;
      master.connect(ctx.destination);
      this.ctx = ctx;
      this.master = master;
      return ctx;
    } catch {
      return null;
    }
  }

  /** Call from a user gesture — safe to call repeatedly. iOS field reports
   *  ("no sound on iPhone") traced to three gaps, each handled here:
   *  1. resume() alone doesn't always unlock older iOS — a source node must
   *     START inside the gesture, so we play a 1-frame silent buffer;
   *  2. iOS re-suspends the context on backgrounding/calls — resume() must be
   *     retried, not fired once (see play() and the visibility listener);
   *  3. the ringer switch mutes WebAudio entirely unless a media element is
   *     playing — the looping silent <audio> promotes us to playback audio. */
  unlock(): void {
    const ctx = this.ensure();
    if (!ctx) return;
    if (ctx.state === 'suspended') void ctx.resume();
    try {
      const buf = ctx.createBuffer(1, 1, ctx.sampleRate);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start(0);
    } catch {
      /* warm-up is best-effort */
    }
    if (isIOS() && this.mediaShim === null) {
      try {
        const a = new Audio(SILENT_WAV);
        a.loop = true;
        a.setAttribute('playsinline', '');
        void a.play().catch(() => {
          this.mediaShim = null; // retry on a later gesture
        });
        this.mediaShim = a;
      } catch {
        this.mediaShim = null;
      }
    }
  }

  /** Re-arm after visibility changes: iOS suspends the context and, without
   *  this, the whole rest of the session is silent. */
  installResumeHooks(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.ctx?.state === 'suspended') {
        void this.ctx.resume();
      }
    });
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master) this.master.gain.value = muted ? 0 : MASTER_VOLUME;
  }

  isMuted(): boolean {
    return this.muted;
  }

  play(sfx: Sfx): void {
    if (this.muted) return;
    const ctx = this.ensure();
    const master = this.master;
    if (!ctx || !master) return;
    if (ctx.state !== 'running') {
      // iOS re-suspends after interruptions; retry so the NEXT sound works.
      void ctx.resume();
      return;
    }
    scheduleSfx(sfx, ctx, master, ctx.currentTime);
  }
}

export const audio = new AudioKit();

// --- offline render (debug builds only) --------------------------------------

export interface RenderedSfx {
  sampleRate: number;
  /** Mono, pre-master (see below). */
  channelData: number[];
}

/** Longest SFX is ~0.44 s; one second of headroom covers every one of them and
 *  the render is trimmed to the scheduled tail anyway. */
const RENDER_SECONDS = 1.2;

/**
 * Renders one SFX into an OfflineAudioContext — the input to
 * `npm run visual:audio` (WAVs + spectrograms + level checks). Debug builds only:
 * the `__BEATME_DEBUG__` guard makes the body dead code in a shipped build, so
 * rollup drops this function and its `OfflineAudioContext` reference with it
 * (grep the normal dist for `renderSfx` — it is not there).
 *
 * The graph is connected straight to the offline destination, i.e. **pre-master**:
 * `MASTER_VOLUME` is one uniform scalar (−11 dB) applied to every SFX equally, so
 * leaving it out changes no relative balance and keeps the level checks about the
 * synthesis rather than about the output-gain constant.
 */
export async function renderSfx(name: Sfx, sampleRate = 48_000): Promise<RenderedSfx> {
  if (!__BEATME_DEBUG__) throw new Error('renderSfx: debug builds only');
  const ctx = new OfflineAudioContext(1, Math.ceil(RENDER_SECONDS * sampleRate), sampleRate);
  const tail = scheduleSfx(name, ctx, ctx.destination, 0);
  const rendered = await ctx.startRendering();
  const frames = Math.min(rendered.length, Math.ceil(tail * sampleRate));
  return {
    sampleRate: rendered.sampleRate,
    channelData: Array.from(rendered.getChannelData(0).subarray(0, frames)),
  };
}
